import { useEffect, useRef, useState } from "react";

/**
 * Max slippage. Not a display preference: the gateway derives the signed floor
 * from it, so every new value re-prices the draft. `activeBps` is null when the
 * gateway did not echo the tolerance, and then no preset claims to be applied.
 */

/** Presets, in basis points: 0.1% / 0.5% / 1%. */
export const SLIPPAGE_PRESETS_BPS = [10, 50, 100] as const;

/** The gateway's own default; also the SPA's, so a fresh card matches a fresh quote. */
export const DEFAULT_SLIPPAGE_BPS = 50;

/** Widest tolerance the control will send: 50%. Beyond that it is not slippage. */
const MAX_SLIPPAGE_BPS = 5_000;

const percentFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

/** 10 → "0.1", 50 → "0.5", 100 → "1". */
export function bpsToPercentText(bps: number): string {
  return percentFormatter.format(bps / 100);
}

/**
 * A percent string → basis points, or null when it is not a tolerance we would
 * send. Truncated (not rounded) past two decimals — bps is the wire precision.
 */
function percentToBps(input: string): number | null {
  const cleaned = input.trim();
  if (cleaned === "" || !/^\d*\.?\d*$/.test(cleaned)) return null;
  const percent = Number(cleaned);
  if (!Number.isFinite(percent)) return null;
  const bps = Math.trunc(percent * 100);
  if (bps <= 0 || bps > MAX_SLIPPAGE_BPS) return null;
  return bps;
}

function GearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M8 10.1a2.1 2.1 0 100-4.2 2.1 2.1 0 000 4.2z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path
        d="M13 8c0-.4 0-.7-.1-1l1.3-1-1.4-2.4-1.6.6a5 5 0 00-1.7-1L9.2 1.5H6.8L6.5 3.2a5 5 0 00-1.7 1l-1.6-.6L1.8 6l1.3 1a6 6 0 000 2l-1.3 1 1.4 2.4 1.6-.6a5 5 0 001.7 1l.3 1.7h2.4l.3-1.7a5 5 0 001.7-1l1.6.6L14.2 10l-1.3-1c.1-.3.1-.6.1-1z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface SlippageControlProps {
  valueBps: number;
  /**
   * The tolerance the gateway confirmed it applied, or null when it said
   * nothing. Only this fills a preset in.
   */
  activeBps: number | null;
  onChange: (bps: number) => void;
  disabled?: boolean;
}

export function SlippageControl({
  valueBps,
  activeBps,
  onChange,
  disabled = false,
}: SlippageControlProps) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const handlePreset = (bps: number) => {
    setCustom("");
    if (bps !== valueBps) onChange(bps);
  };

  const handleCustom = (text: string) => {
    setCustom(text);
    const bps = percentToBps(text);
    if (bps !== null && bps !== valueBps) onChange(bps);
  };

  const isCustom =
    activeBps !== null && !SLIPPAGE_PRESETS_BPS.includes(activeBps as (typeof SLIPPAGE_PRESETS_BPS)[number]);

  return (
    <div className="slippage" ref={rootRef}>
      <button
        type="button"
        className="icon-button slippage-trigger"
        aria-label="Slippage settings"
        aria-haspopup="dialog"
        aria-expanded={open}
        title={`Max slippage ${bpsToPercentText(valueBps)}%`}
        onClick={() => setOpen((v) => !v)}
      >
        <GearIcon />
      </button>

      {open && (
        <div className="slippage-popover" role="dialog" aria-label="Max slippage">
          <span className="slippage-title">Max slippage</span>
          <div className="slippage-presets">
            {SLIPPAGE_PRESETS_BPS.map((bps) => (
              <button
                key={bps}
                type="button"
                className={
                  activeBps === bps ? "slippage-preset slippage-preset-active" : "slippage-preset"
                }
                aria-pressed={activeBps === bps}
                disabled={disabled}
                onClick={() => handlePreset(bps)}
              >
                {bpsToPercentText(bps)}%
              </button>
            ))}
            <span
              className={
                isCustom ? "slippage-custom slippage-custom-active" : "slippage-custom"
              }
            >
              <input
                type="text"
                inputMode="decimal"
                className="slippage-custom-input"
                aria-label="Custom slippage percent"
                placeholder={bpsToPercentText(valueBps)}
                value={custom}
                disabled={disabled}
                onChange={(e) => handleCustom(e.target.value)}
              />
              <span aria-hidden="true">%</span>
            </span>
          </div>
          <p className="slippage-note">
            {activeBps === null
              ? "This gateway prices with its own tolerance — the minimum below is the floor your order will actually carry."
              : "The floor your order carries on chain, not a display setting."}
          </p>
        </div>
      )}
    </div>
  );
}
